import React, { useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { getLocalTimeZone, today } from '@internationalized/date'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Select } from 'components/Select/Select'
import { Item } from '@react-stately/collections'
import Styles from './CampaignDatePicker.module.scss'

interface CampaignDatePickerProps {
  startDate: string
  length: number
  onDateChange: (startDate: string, endDate: string, length: number) => void
  minDate?: string
}

const LENGTH_OPTIONS = [
  { key: 1, label: '1 week' },
  { key: 2, label: '2 weeks' },
  { key: 3, label: '3 weeks' },
  { key: 4, label: '4 weeks' },
]

export const CampaignDatePicker = ({ startDate, length, onDateChange, minDate }: CampaignDatePickerProps) => {
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localLength, setLocalLength] = useState(length)

  const computeEndDate = (start: string, weeks: number) =>
    dayjs(start)
      .add(weeks * 7 - 1, 'day')
      .format('YYYY-MM-DD')

  const endDate = computeEndDate(localStartDate, localLength)

  const handleDateChange = useCallback(
    (date: ReturnType<typeof dayjs> | undefined) => {
      if (!date) return
      const newStart = date.format('YYYY-MM-DD')
      setLocalStartDate(newStart)
      onDateChange(newStart, computeEndDate(newStart, localLength), localLength)
    },
    [localLength, onDateChange],
  )

  const handleLengthChange = useCallback(
    (key: React.Key) => {
      const newLength = Number(key)
      setLocalLength(newLength)
      onDateChange(localStartDate, computeEndDate(localStartDate, newLength), newLength)
    },
    [localStartDate, onDateChange],
  )

  const disableBefore = minDate ?? today(getLocalTimeZone()).add({ days: 1 }).toString()

  return (
    <div className={Styles.campaignDatePickerContainer}>
      <div className={Styles.campaignDatePickerRow}>
        <Text variant="secondary" color="var(--dark-40)">
          Starts on
        </Text>
        <DateTimePicker
          disableDatesBefore={disableBefore}
          dateTime={dayjs(localStartDate)}
          setDateTime={handleDateChange}
          showTimeControls={false}
          showActions
          useSqueeze={false}
          popoverClassName={Styles.popover}
        >
          <Button variant="secondary" size="lg" endIcon={ChevronDown2}>
            {dayjs(localStartDate).format('ddd, MMM D')}
          </Button>
        </DateTimePicker>
        <Text variant="secondary" color="var(--dark-40)">
          Length
        </Text>
        <Select
          selectedKey={String(localLength)}
          variant="secondary"
          size="lg"
          onSelectionChange={handleLengthChange}
          aria-label="Campaign length"
        >
          {LENGTH_OPTIONS.map((opt) => (
            <Item key={opt.key} textValue={opt.label}>
              <Text variant="primary" color="inherit">
                {opt.label}
              </Text>
            </Item>
          ))}
        </Select>
        <Text variant="secondary" color="var(--dark-40)">
          Ends {dayjs(endDate).format('ddd, MMM D')}
        </Text>
      </div>
    </div>
  )
}
